import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SectionTitle from "@/components/base/SectionTitle";
import { booklists, faqs, hotTopics, rankData, readerComments } from "@/mocks/mangaData";

function Hero() {
  return (
    <section className="relative w-full">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20manga%20reader%20community%20hero%20illustration%2C%20a%20cozy%20bookshelf%20and%20reading%20corner%20with%20open%20manga%20volumes%2C%20warm%20paper%20lantern%20and%20ink%20texture%20background%2C%20artistic%20storybook%20mood%2C%20warm%20amber%20cream%20and%20muted%20red%20palette%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=hero-manga-main&orientation=landscape"
          alt="漫画e站同好交流聚集地的温暖阅读场景插画"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50"></div>
      </div>

      <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6 md:py-32">
        <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-medium text-background-50 backdrop-blur">
          <i className="ri-fire-line text-sm"></i>
          每周更新的同好交流聚集地
        </span>
        <h1 className="mt-6 max-w-3xl font-heading text-3xl font-black leading-tight tracking-wide text-background-50 md:text-5xl">
          漫画e站评论区
          <span className="mt-1 block">与热门话题，都在这里</span>
        </h1>
        <p className="mt-5 max-w-xl text-sm leading-7 text-background-100/90 md:text-base">
          汇总读者吐槽与热门话题，整理评分排行和推荐书单，帮你快速找到同好，看看大家都在追什么、避雷什么。
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to="/topic"
            className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            看看热门话题
          </Link>
          <Link
            to="/booklist"
            className="whitespace-nowrap rounded-full border border-background-50/60 bg-background-50/10 px-6 py-3 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
          >
            领取推荐书单
          </Link>
        </div>

        <dl className="mt-12 grid w-full max-w-2xl grid-cols-3 gap-4">
          {[
            { num: "1.2 万+", label: "活跃漫友" },
            { num: "860+", label: "评分作品" },
            { num: "40+", label: "专题书单" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-background-50/10 p-4 backdrop-blur">
              <dt className="order-2 mt-1 text-xs text-background-100/80">{s.label}</dt>
              <dd className="font-heading text-xl font-bold text-background-50 md:text-2xl">{s.num}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function TopicCard({ t }: { t: (typeof hotTopics)[number] }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <div className="relative h-44 w-full overflow-hidden">
        <img
          src={t.cover}
          alt={`${t.title}配图`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
          {t.tag}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to="/topic" className="transition-colors hover:text-primary-700">
            {t.title}
          </Link>
        </h3>
        <p className="mt-2 flex-1 text-sm leading-6 text-foreground-600">{t.excerpt}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-heart-3-line text-accent-500"></i>
              {t.likes}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {t.comments}
            </span>
          </span>
          <span>{t.date}</span>
        </div>
      </div>
    </article>
  );
}

function CommentsSection() {
  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <SectionTitle
            eyebrow="读者吐槽"
            title="评论区真实吐槽，笑点与共鸣并存"
            desc="这里的每一条，都来自真实追番的漫友。无论是被刀到破防，还是挖到宝藏，都欢迎留下你的声音。"
          />
          <Link
            to="/community"
            className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            查看更多吐槽 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {readerComments.slice(0, 3).map((c) => (
            <article
              key={c.id}
              className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-5"
            >
              <div className="flex items-center gap-3">
                <img src={c.avatar} alt={`${c.user}的头像`} className="h-11 w-11 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{c.user}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {c.level} · {c.time}
                  </p>
                </div>
                <span className="ml-auto rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                  {c.topic}
                </span>
              </div>
              <p className="mt-4 flex-1 text-sm leading-7 text-foreground-700">“{c.text}”</p>
              <div className="mt-4 flex items-center gap-1 text-xs text-foreground-500">
                <i className="ri-thumb-up-line"></i>
                <span>{c.likes} 位漫友赞同</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function RankPreview() {
  return (
    <section className="w-full py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <SectionTitle
            eyebrow="评分排行"
            title="本周读者口碑排行 TOP 8"
            desc="综合剧情、画工、人设与更新质量四维度的实时口碑，帮你快速锁定本周最值得追的作品。"
          />
          <Link
            to="/rank"
            className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            查看完整排行 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {rankData.map((r) => (
            <Link
              key={r.id}
              to="/rank"
              className="group flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
            >
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg font-heading text-base font-bold ${
                  r.rank <= 3
                    ? "bg-primary-500 text-background-50"
                    : "bg-secondary-100 text-secondary-800"
                }`}
              >
                {r.rank}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate font-heading text-base font-bold text-foreground-950">
                    {r.title}
                  </h3>
                  <span className="shrink-0 rounded-full bg-background-200 px-2 py-0.5 text-xs text-foreground-600">
                    {r.genre}
                  </span>
                </div>
                <p className="mt-1 text-xs text-foreground-500">评分 {r.score} · {r.votes.toLocaleString()} 人参与</p>
              </div>
              <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-primary-700">
                {r.score}
                {r.change === "up" && <i className="ri-arrow-up-line text-xs text-accent-500"></i>}
                {r.change === "down" && <i className="ri-arrow-down-line text-xs text-secondary-600"></i>}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function BooklistSection() {
  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <SectionTitle
            eyebrow="推荐书单"
            title="编辑精选 · 按口味入座"
            desc="无论是想被治愈、想烧脑，还是想找回热血，总有一份书单在等你打开。每周更新，常看常新。"
          />
          <Link
            to="/booklist"
            className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            全部书单 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {booklists.map((b) => (
            <article
              key={b.id}
              className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
            >
              <div className="relative h-60 w-full overflow-hidden">
                <img
                  src={b.cover}
                  alt={`${b.title}封面`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-full bg-background-950/70 px-3 py-1 text-xs text-background-50 backdrop-blur">
                  {b.count} 部作品
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <span className="w-fit rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                  {b.tag}
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                  <Link to="/booklist" className="transition-colors hover:text-primary-700">
                    {b.title}
                  </Link>
                </h3>
                <p className="mt-2 flex-1 text-sm leading-6 text-foreground-600">{b.desc}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-secondary-500"></i>
                    {b.rating}
                  </span>
                  <span>更新于 {b.updated}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <section className="w-full py-14 md:py-20">
      <div className="mx-auto max-w-3xl px-4 md:px-6">
        <SectionTitle
          align="center"
          eyebrow="常见问题"
          title="关于漫画e站，你想知道的都在这里"
          desc="评分、吐槽、书单与话题，都有明确的规则与玩法，看看有没有解答你的疑惑。"
        />
        <div className="mt-8 space-y-3">
          {faqs.map((f, i) => {
            const open = openIdx === i;
            return (
              <div
                key={f.q}
                className="overflow-hidden rounded-xl border border-background-200 bg-background-50"
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <h3 className="text-sm font-semibold text-foreground-950 md:text-base">{f.q}</h3>
                  <i
                    className={`ri-add-line shrink-0 text-lg text-primary-600 transition-transform ${
                      open ? "rotate-45" : ""
                    }`}
                  ></i>
                </button>
                {open && (
                  <p className="px-5 pb-5 text-sm leading-7 text-foreground-600">{f.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="w-full px-4 pb-14 md:px-6 md:pb-20">
      <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20stylized%20manga%20reading%20scene%20with%20multiple%20open%20books%20and%20warm%20bokeh%20lantern%20lights%2C%20artistic%20ink%20and%20paper%20texture%2C%20warm%20amber%20and%20red%20tones%2C%20inviting%20community%20atmosphere%2C%20high%20detail%2C%20no%20text&width=1600&height=500&seq=cta-manga-band&orientation=landscape"
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/70"></div>
        </div>
        <div className="relative flex w-full flex-col items-center px-6 py-16 text-center md:py-20">
          <h2 className="max-w-2xl font-heading text-2xl font-bold text-background-50 md:text-3xl">
            还没找到和你口味相投的同好？
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-7 text-background-100/85">
            加入漫画e站讨论区，分享你的追番日常、吐槽与安利，让喜欢的事有人一起聊。
          </p>
          <Link
            to="/topic"
            className="mt-7 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            立即加入讨论
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Navbar />
      <main>
        <Hero />
        <section className="w-full py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <SectionTitle
                eyebrow="热门话题"
                title="大家最近都在追什么？"
                desc="新番热议、补番指南、同人讨论与避雷现场，每周精选最值得参与的话题，快来看看有没有你的主场。"
              />
              <Link
                to="/topic"
                className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
              >
                全部话题 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {hotTopics.map((t) => (
                <TopicCard key={t.id} t={t} />
              ))}
            </div>
          </div>
        </section>
        <CommentsSection />
        <RankPreview />
        <BooklistSection />
        <FaqSection />
        <CtaBanner />
      </main>
      <Footer />
    </>
  );
}